import { useQuery } from "@tanstack/react-query";
import { findNearestBuoys } from "./buoy-stations.js";

export interface BuoyReading {
  timestamp: Date;
  waveHeight: number | null; // feet
  dominantPeriod: number | null; // seconds
  meanWaveDirection: number | null; // degrees
  waterTemp: number | null; // fahrenheit
  windSpeed: number | null; // m/s (NDBC realtime2 format)
  windDirection: number | null; // degrees
  gustSpeed: number | null; // m/s (NDBC realtime2 format)
}

const METERS_TO_FEET = 3.28084;
const CELSIUS_TO_FAHRENHEIT = (c: number) => c * 1.8 + 32;

/**
 * Freshness window for buoy readings used in surf decisions. NDBC
 * stations normally update every 10–20 min; 90 min catches real outages
 * without tripping on routine delays.
 */
export const STALE_READING_MINUTES = 90;

function parseValue(val: string): number | null {
  // NDBC missing-value sentinels: `MM` for strings, and numeric columns
  // use a 9-train (99, 99.0, 99.00, 999, 999.0, 9999, 9999.0, ...).
  // A plain literal list missed `99.00` (two decimals) and `9999`, so a
  // sentinel could leak through as a real "99-second" dominant period.
  if (val === "MM") return null;
  if (/^9{2,4}(\.0+)?$/.test(val)) return null;
  const num = parseFloat(val);
  return isNaN(num) ? null : num;
}

function parseDirectionValue(val: string): number | null {
  // Direction columns (WDIR/MWD) can legitimately read 99° — only 999
  // and up is a sentinel there, unlike heights/periods/speeds where 99
  // is already unphysical.
  if (val === "MM") return null;
  if (/^9{3,4}(\.0+)?$/.test(val)) return null;
  const num = parseFloat(val);
  return isNaN(num) ? null : num;
}

/**
 * Parse NDBC realtime2 text format using the header row for column indices.
 * This is resilient to NDBC reordering or adding columns.
 */
export function parseBuoyData(text: string): BuoyReading[] {
  const lines = text.trim().split("\n");
  // First line is header names, second is units — need at least one data row
  if (lines.length < 3) return [];

  // Parse header row to find column indices
  const headers = lines[0].replace(/^#\s*/, "").trim().split(/\s+/);
  const col = (name: string): number => headers.indexOf(name);

  const iYY = col("YY");
  const iMM = col("MM");
  const iDD = col("DD");
  const iHH = col("hh");
  const iMN = col("mm");
  const iWDIR = col("WDIR");
  const iWSPD = col("WSPD");
  const iGST = col("GST");
  const iWVHT = col("WVHT");
  const iDPD = col("DPD");
  const iMWD = col("MWD");
  const iWTMP = col("WTMP");

  // Require at minimum the date columns
  if (iYY < 0 || iMM < 0 || iDD < 0 || iHH < 0 || iMN < 0) return [];

  const readings: BuoyReading[] = [];

  for (let i = 2; i < lines.length && i < 26; i++) {
    // Get latest 24 readings
    const parts = lines[i].trim().split(/\s+/);
    if (parts.length < headers.length) continue;

    // NDBC realtime2 timestamps are UTC. `new Date(y, m, d, h, mn)`
    // interprets its arguments as local time — on any non-UTC client
    // the result skewed by the tz offset, silently defeating the
    // stale-reading gate for users east/west of GMT.
    const timestamp = new Date(
      Date.UTC(
        parseInt(parts[iYY]),
        parseInt(parts[iMM]) - 1,
        parseInt(parts[iDD]),
        parseInt(parts[iHH]),
        parseInt(parts[iMN]),
      ),
    );

    const waveHeightM = iWVHT >= 0 ? parseValue(parts[iWVHT]) : null;
    const waterTempC = iWTMP >= 0 ? parseValue(parts[iWTMP]) : null;

    readings.push({
      timestamp,
      waveHeight: waveHeightM !== null ? waveHeightM * METERS_TO_FEET : null,
      dominantPeriod: iDPD >= 0 ? parseValue(parts[iDPD]) : null,
      meanWaveDirection: iMWD >= 0 ? parseDirectionValue(parts[iMWD]) : null,
      waterTemp: waterTempC !== null ? CELSIUS_TO_FAHRENHEIT(waterTempC) : null,
      windSpeed: iWSPD >= 0 ? parseValue(parts[iWSPD]) : null,
      windDirection: iWDIR >= 0 ? parseDirectionValue(parts[iWDIR]) : null,
      gustSpeed: iGST >= 0 ? parseValue(parts[iGST]) : null,
    });
  }

  return readings;
}

async function fetchBuoyData(stationId: string): Promise<BuoyReading[]> {
  const url = `/api/buoys?station=${stationId}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch buoy data for station ${stationId}`);
  }
  const text = await response.text();
  return parseBuoyData(text);
}

export function useBuoyData(stationId: string) {
  return useQuery({
    queryKey: ["buoy", stationId],
    queryFn: () => fetchBuoyData(stationId),
    staleTime: 30 * 60 * 1000, // 30 minutes
    refetchInterval: 30 * 60 * 1000,
    retry: 2,
  });
}

/**
 * Location-aware buoy hook — finds and fetches the nearest NDBC buoy stations.
 */
export function useNearestBuoyData(lat: number | null, lon: number | null) {
  const result = lat != null && lon != null ? findNearestBuoys(lat, lon) : null;

  const nearshoreId = result?.nearshore?.id ?? null;
  const offshoreId = result?.offshore?.id ?? null;

  const nearshore = useQuery({
    queryKey: ["buoy", nearshoreId],
    queryFn: () => fetchBuoyData(nearshoreId!),
    enabled: nearshoreId !== null,
    staleTime: 30 * 60 * 1000,
    refetchInterval: 30 * 60 * 1000,
    retry: 2,
  });

  const offshore = useQuery({
    queryKey: ["buoy", offshoreId],
    queryFn: () => fetchBuoyData(offshoreId!),
    enabled: offshoreId !== null,
    staleTime: 30 * 60 * 1000,
    refetchInterval: 30 * 60 * 1000,
    retry: 2,
  });

  return {
    nearshore: nearshoreId ? { ...nearshore, station: result!.nearshore! } : null,
    offshore: offshoreId ? { ...offshore, station: result!.offshore! } : null,
    isLoading:
      (nearshoreId ? nearshore.isLoading : false) ||
      (offshoreId ? offshore.isLoading : false),
    hasData: nearshoreId !== null || offshoreId !== null,
  };
}

/**
 * Return the most recent buoy reading, optionally filtering out stale
 * ones. NDBC stations normally update every 10–20 minutes, so anything
 * meaningfully older than that (maintenance outage, power loss, storm
 * damage) is old enough to mislead surf decisions.
 *
 * Pass `maxAgeMinutes` to enforce a freshness window; omit it to get the
 * previous always-return-latest behaviour. When the latest reading is
 * older than the window, `null` is returned so callers can render a
 * "no data" state instead of acting on stale numbers.
 */
export function getLatestReading(
  readings: BuoyReading[] | undefined,
  maxAgeMinutes?: number,
  now: Date = new Date(),
): BuoyReading | null {
  if (!readings || readings.length === 0) return null;
  const latest = readings[0];
  if (maxAgeMinutes == null) return latest;
  const ageMs = now.getTime() - latest.timestamp.getTime();
  if (ageMs > maxAgeMinutes * 60 * 1000) return null;
  return latest;
}

export function getDirectionLabel(degrees: number | null): string {
  if (degrees === null) return "N/A";
  const directions = [
    "N",
    "NNE",
    "NE",
    "ENE",
    "E",
    "ESE",
    "SE",
    "SSE",
    "S",
    "SSW",
    "SW",
    "WSW",
    "W",
    "WNW",
    "NW",
    "NNW",
  ];
  const index = Math.round(degrees / 22.5) % 16;
  return directions[index];
}
